import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../hooks/use-current-user";
import { Role } from "@mestergo/shared";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";
import { useEffect, useRef, useState, useCallback } from "react";
import { ResponseStatus } from "@mestergo/shared";

const VIEW_MODE_KEY = "mesterin_view_mode";

interface LeadItem {
  id: string;
  matchScore: string;
  responseStatus: ResponseStatus;
  request: {
    id: string;
    rawText: string;
    status: string;
    selectedMode: string | null;
    city?: string | null;
    category?: {
      nameRo: string;
      nameRu?: string | null;
    } | null;
    client: {
      displayName?: string | null;
      firstName?: string | null;
      telegramUsername?: string | null;
      phone?: string | null;
    } | null;
    clientProfile: {
      ratingAvg: string;
      ratingCount: number;
    } | null;
  } | null;
}

interface RequestListItem {
  id: string;
  rawText: string;
  status: string;
  city?: string | null;
  scheduledFor?: string | null;
  category?: {
    nameRo: string;
    nameRu?: string | null;
  } | null;
}

interface JobItem {
  id: string;
  request: {
    id: string;
    rawText: string;
    status: string;
    city?: string | null;
    scheduledFor?: string | null;
    category?: {
      nameRo: string;
      nameRu?: string | null;
    } | null;
    client: {
      displayName?: string | null;
      firstName?: string | null;
      telegramUsername?: string | null;
      phone?: string | null;
    } | null;
    clientProfile: {
      ratingAvg: string;
      ratingCount: number;
    } | null;
  } | null;
}

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { locale, t } = useLocale();
  const isHandyman = user?.roles.includes(Role.Handyman);
  const isClient = user?.roles.includes(Role.Client);
  const hasBothRoles = isHandyman && isClient;

  const [viewMode, setViewMode] = useState<"client" | "handyman">(() => {
    const stored = localStorage.getItem(VIEW_MODE_KEY);
    if (stored === "client" || stored === "handyman") return stored;
    return "handyman";
  });

  const switchView = useCallback((mode: "client" | "handyman") => {
    localStorage.setItem(VIEW_MODE_KEY, mode);
    setViewMode(mode);
  }, []);

  // If user only has one role, force that view
  const activeView = hasBothRoles ? viewMode : (isHandyman ? "handyman" : "client");

  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [requests, setRequests] = useState<RequestListItem[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingLeadId, setPendingLeadId] = useState<string | null>(null);
  const previousStatusesRef = useRef<Record<string, ResponseStatus>>({});

  useEffect(() => {
    if (activeView !== "handyman" || !isHandyman) return;
    let cancelled = false;

    const loadLeads = async () => {
      const [{ data: leadsData }, { data: jobsData }] = await Promise.all([
        api.get<LeadItem[]>("/handymen/me/leads"),
        api.get<JobItem[]>("/handymen/me/jobs"),
      ]);
      if (cancelled) return;

      setLeads(leadsData);
      setJobs(jobsData);

      const nextStatuses = Object.fromEntries(leadsData.map((lead) => [lead.id, lead.responseStatus]));
      for (const lead of leadsData) {
        const previousStatus = previousStatusesRef.current[lead.id];
        if (previousStatus === ResponseStatus.Pending && lead.responseStatus === ResponseStatus.Withdrawn) {
          setNotice(t.requestTakenNotice);
        }
      }
      previousStatusesRef.current = nextStatuses;
    };

    void loadLeads();
    const interval = window.setInterval(() => { void loadLeads(); }, 5000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [activeView, isHandyman, t.requestTakenNotice]);

  useEffect(() => {
    if (activeView !== "client") return;
    let cancelled = false;

    const loadRequests = async () => {
      const { data } = await api.get<RequestListItem[]>("/requests");
      if (!cancelled) setRequests(data);
    };

    void loadRequests();
    const interval = window.setInterval(() => { void loadRequests(); }, 5000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [activeView]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  const visibleLeads = leads.filter(
    (lead) => lead.responseStatus !== ResponseStatus.Withdrawn && lead.responseStatus !== ResponseStatus.Declined,
  );
  const visibleJobs = jobs.filter((job) => Boolean(job.request));

  const formatDateTime = (value?: string | null) => {
    if (!value) return t.anytime;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t.anytime;
    return date.toLocaleString();
  };

  const getLeadStatusLabel = (status: ResponseStatus) => {
    switch (status) {
      case ResponseStatus.Accepted: return t.leadStatusAccepted;
      case ResponseStatus.Declined: return t.leadStatusDeclined;
      case ResponseStatus.Withdrawn: return t.leadStatusWithdrawn;
      default: return t.leadStatusPending;
    }
  };

  const handleLeadAction = async (leadId: string, action: ResponseStatus.Accepted | ResponseStatus.Declined) => {
    setPendingLeadId(leadId);
    try {
      const { data } = await api.put<{ ok: boolean; status: ResponseStatus; message?: string }>(
        `/handymen/me/leads/${leadId}/respond`,
        { action },
      );
      if (data.message) setNotice(data.message);
      setLeads((current) => current.map((lead) => (
        lead.id === leadId ? { ...lead, responseStatus: data.status } : lead
      )));
    } catch {
      setNotice(t.requestTakenNotice);
    } finally {
      setPendingLeadId(null);
    }
  };

  const name = user?.firstName ?? user?.displayName ?? t.guest;
  const initials = name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          {/* Avatar + name */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-xs font-bold text-orange-600">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-gray-400 leading-tight">{t.welcomeBack}</div>
              <div className="text-sm font-bold text-gray-900 truncate max-w-[120px]">{name}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {hasBothRoles ? (
              <div className="flex rounded-full bg-gray-100 p-0.5">
                <button
                  type="button"
                  onClick={() => switchView("client")}
                  className={`rounded-full px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                    activeView === "client" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                  }`}
                >
                  👤 Client
                </button>
                <button
                  type="button"
                  onClick={() => switchView("handyman")}
                  className={`rounded-full px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                    activeView === "handyman" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                  }`}
                >
                  👷 Meșter
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/handyman/verification")}
                className="rounded-full bg-orange-50 px-2.5 py-1.5 text-[11px] font-semibold text-orange-600 active:bg-orange-100 transition-colors whitespace-nowrap"
              >
                {isHandyman ? `👷 ${t.manageVerification}` : `👷 ${t.becomeHandyman}`}
              </button>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="flex-1 p-5 space-y-4">
        {/* Notice toast */}
        {notice ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            {notice}
          </div>
        ) : null}

        {activeView === "handyman" ? (
          <>
            {/* Handyman dashboard */}
            <button
              type="button"
              onClick={() => navigate("/handyman/profile")}
              className="w-full rounded-2xl bg-orange-500 px-5 py-4 text-left text-white active:bg-orange-600 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-base font-bold">{t.editProfile}</div>
                  <div className="mt-0.5 text-sm opacity-75">{t.handymanProfileSubtitle}</div>
                </div>
                <div className="text-2xl opacity-60">›</div>
              </div>
            </button>

            {/* Active jobs */}
            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="font-bold text-gray-900">{t.currentJobs}</div>
                {visibleJobs.length > 0 ? (
                  <div className="rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-bold text-orange-600">
                    {visibleJobs.length}
                  </div>
                ) : null}
              </div>
              {visibleJobs.length === 0 ? (
                <div className="rounded-xl bg-gray-50 px-4 py-5 text-center text-sm text-gray-400">{t.noJobsShort}</div>
              ) : (
                <div className="space-y-3">
                  {visibleJobs.map((job) => {
                    if (!job.request) return null;
                    const clientName = job.request.client?.displayName || job.request.client?.firstName || t.guest;
                    const categoryName = job.request.category
                      ? locale === "ru" ? job.request.category.nameRu || job.request.category.nameRo : job.request.category.nameRo
                      : "-";
                    const telegramLink = job.request.client?.telegramUsername
                      ? `https://t.me/${job.request.client.telegramUsername}`
                      : null;
                    return (
                      <div key={job.id} className="rounded-xl border border-gray-100 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{clientName}</div>
                            <div className="mt-0.5 text-xs text-gray-400">{categoryName} · {job.request.city || "-"}</div>
                          </div>
                          <div className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 shrink-0">
                            {t.requestStatusLabels[job.request.status as keyof typeof t.requestStatusLabels] ?? job.request.status}
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-gray-600 line-clamp-2">{job.request.rawText}</div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" onClick={() => navigate(`/handyman/jobs/${job.request?.id}`)}
                            className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700">
                            {t.openRequest}
                          </button>
                          {telegramLink ? (
                            <a href={telegramLink} target="_blank" rel="noreferrer"
                              className="rounded-xl bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">
                              {t.openTelegram}
                            </a>
                          ) : null}
                          {job.request.client?.phone ? (
                            <a href={`tel:${job.request.client.phone}`}
                              className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700">
                              {t.callPhone}
                            </a>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Leads inbox */}
            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="font-bold text-gray-900">{t.leadsTitle}</div>
                {visibleLeads.length > 0 ? (
                  <div className="rounded-full bg-orange-500 px-2.5 py-0.5 text-xs font-bold text-white">
                    {visibleLeads.length}
                  </div>
                ) : null}
              </div>
              {visibleLeads.length === 0 ? (
                <div className="rounded-xl bg-gray-50 px-4 py-5 text-center text-sm text-gray-400">{t.noLeadsShort}</div>
              ) : (
                <div className="space-y-3">
                  {visibleLeads.map((lead) => {
                    if (!lead.request) return null;
                    const clientName = lead.request.client?.displayName || lead.request.client?.firstName || t.guest;
                    const telegramLink = lead.request.client?.telegramUsername
                      ? `https://t.me/${lead.request.client.telegramUsername}`
                      : null;
                    const categoryName = lead.request.category
                      ? locale === "ru" ? lead.request.category.nameRu || lead.request.category.nameRo : lead.request.category.nameRo
                      : "-";
                    const ratingText = lead.request.clientProfile
                      ? `★ ${Number(lead.request.clientProfile.ratingAvg).toFixed(1)}`
                      : "—";
                    const isPending = lead.responseStatus === ResponseStatus.Pending;
                    const isAccepted = lead.responseStatus === ResponseStatus.Accepted;
                    const isActionLoading = pendingLeadId === lead.id;
                    const score = Math.round(Number(lead.matchScore));

                    return (
                      <div key={lead.id} className="rounded-xl border border-gray-100 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{clientName}</div>
                            <div className="mt-0.5 text-xs text-gray-400">{categoryName} · {ratingText}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <div className="rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-bold text-orange-600">
                              {score}%
                            </div>
                            <div className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              isAccepted ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                            }`}>
                              {getLeadStatusLabel(lead.responseStatus)}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-gray-600 line-clamp-2">{lead.request.rawText}</div>
                        <div className="mt-1 text-xs text-gray-400">📍 {lead.request.city || "-"}</div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {telegramLink ? (
                            <a href={telegramLink} target="_blank" rel="noreferrer"
                              className="rounded-xl bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">
                              {t.openTelegram}
                            </a>
                          ) : null}
                          {lead.request.client?.phone ? (
                            <a href={`tel:${lead.request.client.phone}`}
                              className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700">
                              {t.callPhone}
                            </a>
                          ) : null}
                          {isPending ? (
                            <>
                              <button type="button" disabled={isActionLoading}
                                onClick={() => void handleLeadAction(lead.id, ResponseStatus.Accepted)}
                                className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                                {t.acceptLead}
                              </button>
                              <button type="button" disabled={isActionLoading}
                                onClick={() => void handleLeadAction(lead.id, ResponseStatus.Declined)}
                                className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 disabled:opacity-50">
                                {t.declineLead}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Client dashboard */}
            <button
              type="button"
              onClick={() => navigate("/request")}
              className="w-full rounded-2xl bg-gray-900 px-5 py-5 text-left text-white active:bg-gray-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-xl">🔧</div>
                <div>
                  <div className="font-bold">+ {t.newRequest}</div>
                  <div className="mt-0.5 text-sm opacity-70">{t.newRequestHint}</div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate("/handymen")}
              className="w-full rounded-2xl bg-white px-5 py-4 text-left shadow-sm active:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900">{t.browseHandymen}</div>
                  <div className="mt-0.5 text-sm text-gray-500">{t.handymenTitle}</div>
                </div>
                <div className="text-2xl text-gray-300">›</div>
              </div>
            </button>

            {/* Recent requests */}
            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="font-bold text-gray-900">{t.recentRequests}</div>
                {requests.length > 0 ? (
                  <div className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
                    {requests.length}
                  </div>
                ) : null}
              </div>
              {requests.length === 0 ? (
                <div className="rounded-xl bg-gray-50 px-4 py-5 text-center text-sm text-gray-400">{t.noRequestsShort}</div>
              ) : (
                <div className="space-y-3">
                  {requests.slice(0, 5).map((request) => {
                    const categoryName = request.category
                      ? locale === "ru" ? request.category.nameRu || request.category.nameRo : request.category.nameRo
                      : "-";
                    const statusColors: Record<string, string> = {
                      assigned: "bg-emerald-50 text-emerald-700",
                      completed: "bg-emerald-100 text-emerald-800",
                      matching: "bg-orange-50 text-orange-600",
                      pending: "bg-orange-50 text-orange-600",
                      cancelled: "bg-gray-100 text-gray-500",
                      expired: "bg-gray-100 text-gray-500",
                    };
                    return (
                      <div key={request.id} className="rounded-xl border border-gray-100 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 text-sm text-gray-700 line-clamp-2">{request.rawText}</div>
                          <div className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColors[request.status] ?? "bg-gray-100 text-gray-500"}`}>
                            {t.requestStatusLabels[request.status as keyof typeof t.requestStatusLabels] ?? request.status}
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-400">
                          📍 {request.city || "-"} · {categoryName} · {formatDateTime(request.scheduledFor)}
                        </div>
                        <button type="button" onClick={() => navigate(`/requests/${request.id}`)}
                          className="mt-3 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700">
                          {t.openRequest}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
